import { api } from "@/services/api";

/**
 * The receipt check, client side.
 *
 * NOTHING HERE COMPUTES A DEDUCTION. The preview comes from the server, through
 * the same `CheckReceipt` the save path uses — mirroring the arithmetic in
 * TypeScript would put a second definition of what the farm PAYS one network
 * hop from the first, and the drifted one would be the number an operator reads
 * while deciding whether to accept a lorry.
 *
 * See docs/architecture/receipt-checks.md.
 */

export interface ReceiptCheckPreview {
  bill_qty: number;
  shortage_qty: number;
  moisture_qty: number;
  accepted_qty: number;
  aflatoxin_failed: boolean;
  /** False when nothing was measured — never "checked and passed". */
  checked: boolean;
  /** The material's own standards, so the form can say what it is judging against. */
  moisture_std_pct: number | null;
  aflatoxin_limit: number | null;
  /** The rupee effect on the bill, when a rate is known. */
  deduction_amount: number | null;
}

/**
 * A blank box is NOT zero — it is "not measured", and must reach the API as
 * undefined. A load nobody weighed must never read as a load that weighed
 * nothing.
 *
 * `factor` converts tonnes to the kilograms the quantity is sent in: the
 * weighbridge reading is compared against the billed figure, and a tonne
 * against a kilogram reports a 99.9% shortage on every load.
 */
export function toReading(v: string, factor: number): number | undefined {
  if (v.trim() === "") return undefined;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n * factor : undefined;
}

/** True when any measurement was entered — what makes a preview worth asking for. */
export function hasAnyReading(...values: string[]): boolean {
  return values.some((v) => v.trim() !== "");
}

export const receiptCheckService = {
  preview: (payload: {
    raw_material_id: number;
    qty: number;
    rate_per_unit?: number;
    weighbridge_qty?: number;
    moisture_pct?: number;
    aflatoxin?: number;
  }) =>
    api
      .post<ReceiptCheckPreview>("/raw-material-stock/preview-check", payload)
      .then((r) => r.data),
};
